/**
 * Moving several invoices at once in the staff console.
 *
 * Finance works this screen as a batch — Vikas asked for "one consolidated
 * payment dashboard you process on a daily or weekly basis" — but the only
 * way to move an invoice is its own row menu. Twenty approvals is twenty
 * menus. These shape a selection into one safe action.
 *
 * What is offered is the *intersection* of what the server already said each
 * invoice may do (`next_statuses`), so a bulk action can never push an
 * invoice through a transition the server would refuse for it.
 */

/**
 * Transitions every selected invoice allows, in the order the first invoice
 * listed them. "Move back" transitions are left out: undoing a specific
 * invoice is a correction, not something to do to a batch by accident.
 */
export function commonNextStatuses(invoices) {
  const list = Array.isArray(invoices) ? invoices : [];
  if (list.length === 0) return [];

  const optionsFor = (invoice) =>
    (Array.isArray(invoice && invoice.next_statuses) ? invoice.next_statuses : []).filter(
      (option) => option && option.value && option.direction !== 'back',
    );

  const [first, ...rest] = list;
  return optionsFor(first).filter((option) =>
    rest.every((invoice) => optionsFor(invoice).some((other) => other.value === option.value)),
  );
}

/**
 * One sentence for the run. Partial failure is the normal case worth naming:
 * a silent "done" after three of twenty succeeded is how money goes missing.
 */
export function summariseBulk(outcomes, statusLabel) {
  const results = Array.isArray(outcomes) ? outcomes : [];
  const ok = results.filter((r) => r && r.ok).length;
  const failures = results.filter((r) => r && !r.ok);
  const failed = failures.length;

  const noun = (n) => `${n} invoice${n === 1 ? '' : 's'}`;

  if (failed === 0) {
    return { ok, failed, tone: 'success', message: `${noun(ok)} moved to ${statusLabel}.` };
  }

  const names = failures
    .map((r) => r.name)
    .filter(Boolean)
    .join(', ');
  const tail = names ? ` Could not move: ${names}.` : '';

  if (ok === 0) {
    return { ok, failed, tone: 'error', message: `Nothing moved.${tail}` };
  }
  return {
    ok,
    failed,
    tone: 'warn',
    message: `${noun(ok)} moved to ${statusLabel}, ${failed} failed.${tail}`,
  };
}

/** Selection toggle that returns a new Set. */
export function toggleId(selected, id) {
  const next = new Set(selected || []);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}
